import { Stack, type Theme, useMediaQuery } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import settings from '/@/core/settings';
import QRCodeScanner, { checkVideoInput } from '/@/hooks/qrcode-scanner';
import { _ } from '/@/locale';
import {
    fetchDataByBatch,
    fetchDataByQRCode,
    fetchHistoricalConsultations,
    resetBatchData,
    saveBatchConsultation,
    setBatchData,
    setHistoricalConsultations,
} from '/@/store/bag-quality/actions';
import {
    getActiveBrands,
    getBatchData,
    getHistoricalConsultations,
    getQrCornSeedQualityEnabled,
} from '/@/store/bag-quality/selectors';
import { getInternalProfileEnabled, getSessionCountry, getUser } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import bagImageDK from './assets/bag-quality-DK.png';
import bagImageLT from './assets/bag-quality-LT.png';
import DpsBanner from './components/dps-banner';
import FormCard from './components/form-card';
import LoteHelpModal from './components/lote-help-modal';
import ProbadorPlacaBanner from './components/probador-placa-banner';
import ResultCard from './components/result-card';
import SavedAnalysis from './components/saved-analysis';

const SIDEBAR_WIDTH = 458;

const BagQualityV2 = () => {
    const dispatch = useDispatch();
    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));

    const brand = useSelector((state) => getActiveBrands(state, 'corn')?.[0]);
    const historicalConsultations = useSelector(getHistoricalConsultations);
    const batchData = useSelector(getBatchData);
    const country = useSelector(getSessionCountry);
    const user = useSelector(getUser);
    const internalProfileEnabled = useSelector(getInternalProfileEnabled);
    const qrCornSeedQualityEnabled = useSelector(getQrCornSeedQualityEnabled);

    const [batchInputValue, setBatchInputValue] = useState('');
    const [noteInputValue, setNoteInputValue] = useState('');
    // Note from a saved consultation opened via the sidebar; null while doing a fresh search.
    const [savedNote, setSavedNote] = useState<string | null>(null);
    const [batchError, setBatchError] = useState(false);
    const [canSave, setCanSave] = useState(false);
    const [showQRScanner, setShowQRScanner] = useState(false);
    const [loteModalOpen, setLoteModalOpen] = useState(false);

    const isUruguay = country?.code === 'UY';
    const isLT = settings.app.theme === 'latijereta';

    const refreshHistorical = useCallback(async () => {
        if (!user || !brand?.id) {
            return;
        }
        try {
            const data = await dispatch(fetchHistoricalConsultations({ brand: brand.id }));
            if (data?.response) {
                dispatch(setHistoricalConsultations(data.response));
            }
        } catch (error) {
            console.error('Error fetching historical consultations:', error);
        }
    }, [dispatch, user, brand?.id]);

    useEffect(() => {
        refreshHistorical();
    }, [refreshHistorical]);

    useEffect(() => {
        let active = true;
        checkVideoInput()
            .then((hasVideoInput) => {
                if (active) {
                    setShowQRScanner(!!hasVideoInput);
                }
            })
            .catch((error) => {
                console.error('Error checking video input:', error);
                if (active) {
                    setShowQRScanner(false);
                }
            });
        return () => {
            active = false;
        };
    }, []);

    const applyResult = (result: { response?: { status?: number }; data?: unknown } | undefined) => {
        if (!result) {
            setBatchError(true);
            return false;
        }
        const { response, data } = result;
        dispatch(setBatchData(data));
        const ok = response?.status === 200;
        setBatchError(!ok);
        return ok;
    };

    const searchBatch = async (batch: string) => {
        try {
            const result = await dispatch(fetchDataByBatch({ batch: batch.toUpperCase(), brand: brand?.id }));
            const ok = applyResult(result);
            mpTrackEvent('Seed quality inquiry submitted', {
                state: ok ? 'success' : 'fail',
                search: batch,
                brand: brand?.name,
            });
            dispatch(
                trackEvent({
                    key: ok ? 'bag-quality.search-success' : 'bag-quality.search-error',
                    payload: { search: batch || '', brand: brand?.name || '' },
                })
            );
            setCanSave(ok);
        } catch (error) {
            console.error('Error submitting batch inquiry:', error);
            setBatchError(true);
            dispatch(setBatchData(undefined));
            mpTrackEvent('Seed quality inquiry submitted', { state: 'fail', search: batch, brand: brand?.name });
            dispatch(
                trackEvent({
                    key: 'bag-quality.search-error',
                    payload: { search: batch || '', brand: brand?.name || '' },
                })
            );
        }
    };

    const onSubmit = (event?: React.FormEvent) => {
        event?.preventDefault();
        if (!batchInputValue) {
            return;
        }
        setSavedNote(null);
        searchBatch(batchInputValue);
    };

    const onScanResult = async (qrCode: string) => {
        try {
            setSavedNote(null);
            const result = await dispatch(fetchDataByQRCode({ qrCode, brand: brand?.id }));
            setCanSave(applyResult(result));
        } catch (error) {
            console.error('Error fetching data by QR code:', error);
            setBatchError(true);
            dispatch(setBatchData(undefined));
        }
    };

    const handleSeeBatch = (batch: string, note?: string) => {
        setBatchInputValue(batch);
        setSavedNote(note ?? '');
        searchBatch(batch);
    };

    const handleNewSearch = () => {
        dispatch(resetBatchData());
        setBatchInputValue('');
        setNoteInputValue('');
        setSavedNote(null);
        setBatchError(false);
        setCanSave(false);
    };

    const handleSaveBatch = async () => {
        const id = batchData?.response?.id;
        if (!id) {
            console.error('Cannot save batch: missing batch data or ID');
            return;
        }
        try {
            await dispatch(saveBatchConsultation({ id, note: noteInputValue }));
            mpTrackEvent('Seed quality inquiry saved', { has_note: !!noteInputValue });
            handleNewSearch();
            refreshHistorical();
        } catch (error) {
            console.error('Error saving batch consultation:', error);
        }
    };

    const hasResult = !!batchData && !batchError;
    const savedConsultations = Array.isArray(historicalConsultations) ? historicalConsultations : [];

    return (
        <PageContainer
            header={
                <Header
                    title={_('Calidad de semilla')}
                    subtitle={_(
                        'Accedé a la información de calidad y obtené la placa recomendada para tu híbrido/grado.'
                    )}
                />
            }
        >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={4} alignItems='flex-start' mt={3}>
                <Stack spacing={4} flex={1} minWidth={0} width='100%'>
                    <FormCard
                        value={batchInputValue}
                        error={batchError}
                        onChange={setBatchInputValue}
                        onSubmit={onSubmit}
                        onOpenLoteModal={() => setLoteModalOpen(true)}
                    >
                        {isSmOrLower && showQRScanner && qrCornSeedQualityEnabled && (
                            <QRCodeScanner sx={{ width: '100%', mt: 2 }} qrCodeSuccessCallback={onScanResult} />
                        )}
                    </FormCard>

                    {hasResult && (
                        <ResultCard
                            data={batchData}
                            note={noteInputValue}
                            onNoteChange={setNoteInputValue}
                            onNewSearch={handleNewSearch}
                            onSave={handleSaveBatch}
                            canSave={canSave}
                            showUser={!!user}
                            showDpsAnalysis={(internalProfileEnabled || isLT) && !isUruguay}
                            savedNote={savedNote}
                            brand={brand}
                        />
                    )}
                </Stack>

                <Stack spacing={3} width={{ xs: '100%', md: SIDEBAR_WIDTH }} flexShrink={0}>
                    {!isUruguay && !isLT && <DpsBanner />}
                    {isLT && <ProbadorPlacaBanner />}
                    {user && savedConsultations.length > 0 && (
                        <SavedAnalysis consultations={savedConsultations} onSee={handleSeeBatch} />
                    )}
                </Stack>
            </Stack>

            <LoteHelpModal
                open={loteModalOpen}
                onClose={() => setLoteModalOpen(false)}
                image={isUruguay ? bagImageLT : bagImageDK}
            />
        </PageContainer>
    );
};

export default BagQualityV2;
